import React, { useState } from 'react';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import Icon from './shared/Icon';
import CascaDeModal, { NumeroDaFaixa } from './shared/CascaDeModal';
import SeloDoPapel from './SeloDoPapel';
import LinhaDeItem from './LinhaDeItem';
import FaixaDoRateio from './FaixaDoRateio';
import { RateioDoGrupo } from '../utils/rateioDoGrupo';
import { fmtCNPJ, fmtDate, fmtShortDate, getDaysUntil, getTodayStr } from '../utils/formatters';
import { situacaoDaVigencia, somaVigente } from '../utils/vigenciaItem';
import { temItemForaDeVigencia } from './RodapeMrrDaAssinatura';
import { ramaisDaAssinatura, situacaoDoReajuste, alvoDoDesconto } from '../utils/fichaDaAssinatura';
import { montarPainelDoGrupo } from '../utils/quemUsaQuemPaga';
import { BarraDoGrupo } from './PainelQuemUsaQuemPaga';

/**
 * FICHA DA ASSINATURA: o modal do botão do olho, para CONSULTA.
 * Prévia aprovada: https://claude.ai/artifact/Ci8tFaP5pwQKMng4YzAU8U
 *
 * Os números na frente, a descrição comercial atrás de um clique. O texto do
 * produto serve à proposta, onde o cliente está decidindo; aqui quem abre já
 * sabe o que é uma licença de ramal e quer ver quantos, por quanto e quanto dá.
 * Ver utils/fichaDaAssinatura.ts.
 *
 * ABA "O GRUPO" (prévia https://claude.ai/artifact/7GXj6AqPmFLsjthdRUvw3f): quando
 * a assinatura pertence a um grupo com rateio, a ficha abre POR PADRÃO na visão
 * do grupo, uma camada de leitura sobre assinaturas que continuam separadas
 * (cada empresa com sua fatura, nota e boleto). "Esta empresa" é a ficha de
 * sempre, intacta. Assinatura fora de grupo não ganha aba nenhuma.
 * ⛔ O cálculo e a barra são os MESMOS do painel de Grupos Econômicos
 * (`montarPainelDoGrupo`, `BarraDoGrupo`): um caminho só para os dois números.
 */
interface Props {
  contract: any;
  client: any;
  /** Itens já filtrados pelo local, quando a ficha abre de uma filial. */
  items: any[];
  /** DDRs ativos do escopo (cliente + empresas do grupo, ou só o local). */
  ddrs: any[];
  products: any[];
  rateio: RateioDoGrupo | null;
  nomeDoCliente: (id: number) => string;
  fmt: (v: any) => string;
  locationName?: string;
  footer: React.ReactNode;
  onClose: () => void;
  /** Assinaturas e clientes da base, para a aba "O grupo". Ausentes = sem aba. */
  contratos?: any[];
  clientes?: any[];
  /** Leva ao painel completo do grupo em Grupos Econômicos. */
  onAbrirGrupo?: (matrizId: number) => void;
}

const tipoDoItem = (i: any, products: any[]) => {
  const p = products.find((x: any) => Number(x.id) === Number(i.productId));
  return i.type || p?.type || 'MRR';
};

const TOM_DO_REAJUSTE: Record<string, string> = {
  // Amarelo e âmbar não são dois tons (§4): vencido é danger, próximo é warning.
  vencido: TONE_TEXT.danger,
  proximo: TONE_TEXT.warning,
  ok: 'text-white',
  nenhum: 'text-slate-500',
};

// Tom explícito por status de assinatura (Cancelado/Inativo caem em danger no fallback abaixo).
const STATUS_PILL: Record<string, string> = {
  Ativo: TONE_BADGE.open,
  Suspenso: TONE_BADGE.warning,
};

const FichaDaAssinatura: React.FC<Props> = ({ contract, client, items, ddrs, products, rateio, nomeDoCliente, fmt, locationName, footer, onClose, contratos, clientes, onAbrirGrupo }) => {
  // null = o usuário ainda não escolheu: abre no grupo quando houver grupo.
  const [aba, setAba] = useState<'grupo' | 'empresa' | null>(null);
  const painel = !locationName && contratos ? montarPainelDoGrupo(rateio, contratos, clientes) : null;
  const abaAtiva = painel ? aba ?? 'grupo' : 'empresa';
  const euNoGrupo = painel?.empresas.find((e) => e.clientId === contract.clientId);
  // Descrição recolhida por padrão; o ▸ abre a do item, e só a dele.
  const [abertas, setAbertas] = useState<Set<number>>(new Set());
  const alternar = (idx: number) => setAbertas((prev) => {
    const n = new Set(prev);
    if (n.has(idx)) n.delete(idx); else n.add(idx);
    return n;
  });

  const hoje = getTodayStr();
  const mrrItems = items.filter((i) => tipoDoItem(i, products) === 'MRR');
  const psItems = items.filter((i) => tipoDoItem(i, products) !== 'MRR');
  const vigente = somaVigente(mrrItems, hoje);
  const contratado = mrrItems.reduce((s: number, i: any) => s + (Number(i.totalValue) || 0), 0);
  // Contrato inteiro: o MRR gravado, como a tela de Assinaturas. Por local: a soma vigente do local.
  const mrr = locationName ? vigente : (Number(contract.mrr) || vigente);
  const totalPS = psItems.reduce((s: number, i: any) => s + (Number(i.totalValue) || 0), 0);
  const reajuste = situacaoDoReajuste(contract, getDaysUntil(contract.nextAdjustmentDate), fmtShortDate);
  const nomeExibido = client?.name || client?.tradeName || client?.companyName || '—';

  const numeros: NumeroDaFaixa[] = [
    { k: locationName ? 'MRR do local' : 'MRR', v: fmt(mrr), cls: 'text-green-400', dica: temItemForaDeVigencia(vigente, contratado) ? `Contratado R$ ${fmt(contratado)}; vale hoje R$ ${fmt(vigente)}` : undefined },
    { k: 'Ramais', v: ramaisDaAssinatura(items, products) },
    { k: 'DDRs', v: ddrs.length, dica: 'DDRs ativos' },
    { k: 'Itens', v: items.length },
    ...(totalPS > 0 ? [{ k: 'P&S', v: fmt(totalPS), cls: 'text-orange-400' }] : []),
    {
      k: 'Vigência',
      v: contract.contractStartDate ? fmtShortDate(contract.contractStartDate) : '—',
      dica: contract.contractEndDate ? `Até ${fmtDate(contract.contractEndDate)}` : 'Sem data de término',
    },
    { k: 'Reajuste', v: reajuste.texto, cls: TOM_DO_REAJUSTE[reajuste.tom], dica: reajuste.dica },
  ];

  const linha = (item: any, idx: number, ps: boolean) => {
    const prod = products.find((p: any) => Number(p.id) === Number(item.productId));
    const descricao = item.description || prod?.description;
    const aberta = abertas.has(idx);
    const total = Number(item.totalValue) || 0;
    const negativo = total < 0;
    const situacao = ps ? 'vigente' : situacaoDaVigencia(item, hoje);
    const fora = situacao !== 'vigente';
    const alvo = negativo || /^DESCONTO/i.test(String(item.productCode || prod?.code || '')) ? alvoDoDesconto(item, items, products) : null;
    return (
      <LinhaDeItem
        key={idx}
        codigo={item.productCode || prod?.code || '—'}
        nome={item.name || prod?.name || '—'}
        titulo={prod?.name || item.name}
        complemento={alvo ? `sobre ${alvo}` : null}
        descricao={descricao}
        aberta={aberta}
        onAlternar={() => alternar(idx)}
        esmaecida={fora}
        selos={
          <>
            {fora && (
              <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded-full ${situacao === 'futuro' ? TONE_BADGE.future : TONE_BADGE.neutral}`}>
                {situacao === 'futuro' ? `começa ${fmtShortDate(item.vigenciaInicio)}` : `encerrado ${fmtShortDate(item.vigenciaFim)}`}
              </span>
            )}
            {ps && item.installments > 0 && (
              <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded-full ${TONE_BADGE.ps}`} title={`${item.paidInstallments || 0} parcela(s) já cobrada(s) de ${item.installments}`}>
                {item.paidInstallments || 0}/{item.installments}{(item.paidInstallments || 0) >= item.installments ? ' ✓' : ''}
              </span>
            )}
            {!ps && <SeloDoPapel rateio={rateio} clientId={contract.clientId} item={item} fmt={fmt} />}
          </>
        }
        quantidade={item.quantity || 1}
        unitario={<>{fmt(item.unitValue || 0)}{item.discount > 0 ? <span className='text-amber-300'> −{item.discount}%</span> : null}</>}
        total={fmt(total)}
        tom={negativo ? 'negativo' : fora ? 'apagado' : ps ? 'ps' : 'verde'}
      />
    );
  };

  const noGrupo = !!(painel && abaAtiva === 'grupo');

  return (
    // Cabeçalho sóbrio: o título já diz o que a faixa azul dizia, e em troca
    // entram razão social e CNPJ. A moldura é a CascaDeModal, a mesma da fatura.
    <CascaDeModal
      largura='xl'
      onClose={onClose}
      titulo={locationName ? `${locationName}` : nomeExibido}
      subtitulo={[
        `#${contract.code || contract.id}`,
        locationName ? nomeExibido : null,
        client?.companyName && client.companyName !== nomeExibido ? client.companyName : null,
        client?.cnpj ? fmtCNPJ(client.cnpj) : null,
      ].filter(Boolean).join(' · ')}
      selos={
        <>
          <span className={`shrink-0 text-xs px-2.5 py-0.5 rounded-full ${STATUS_PILL[contract.status] || TONE_BADGE.danger}`}>{contract.status || '—'}</span>
          {rateio && rateio.temRateio !== false && (
            <span className={`shrink-0 text-xs px-2.5 py-0.5 rounded-full ${TONE_BADGE.group}`}>
              grupo{euNoGrupo && euNoGrupo.papel !== 'empresa' ? ` · ${euNoGrupo.papel}` : ''}
            </span>
          )}
          {locationName && <span className={`shrink-0 text-xs px-2.5 py-0.5 rounded-full ${TONE_BADGE.group}`}>local</span>}
        </>
      }
      abas={painel ? [{ chave: 'grupo', rotulo: 'O grupo' }, { chave: 'empresa', rotulo: 'Esta empresa' }] : undefined}
      abaAtiva={abaAtiva}
      onAba={(k) => setAba(k as 'grupo' | 'empresa')}
      faixa={noGrupo && painel ? [
        { k: 'MRR do grupo', v: fmt(painel.mrr), cls: 'text-green-400' },
        { k: 'Empresas', v: painel.empresas.length },
        { k: 'Ramais', v: painel.ramais },
        { k: 'DDRs', v: painel.ddrs, dica: 'DDRs contratados nas assinaturas ativas do grupo' },
        { k: 'Compartilhado', v: fmt(painel.pool), cls: TONE_TEXT.group, dica: 'O que o grupo divide pelo critério do rateio' },
        { k: 'Próprio', v: fmt(painel.proprio), dica: 'O que cada empresa paga só por si (MRR do grupo − compartilhado)' },
      ] : numeros}
      // Rateio na CONSULTA, não só na edição.
      topo={!noGrupo && !locationName ? <FaixaDoRateio rateio={rateio} clientId={contract.clientId} nome={nomeDoCliente} fmt={fmt} compacto /> : undefined}
      corpoClassName={noGrupo ? 'px-5 py-4' : 'px-5 py-3'}
      rodape={
        noGrupo && painel ? (
          <>
            {footer}
            {onAbrirGrupo && rateio && rateio.temRateio !== false && (
              <button
                onClick={() => onAbrirGrupo(rateio.matrizId)}
                className='px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-slate-800 text-slate-200 hover:bg-slate-700'
              >
                <Icon name='building' className='w-4 h-4' />Abrir no Grupos Econômicos
              </button>
            )}
            <span className='ml-auto font-mono text-sm text-green-400'>grupo R$ {fmt(painel.mrr)}</span>
          </>
        ) : (
          <>
            {footer}
            <span className='ml-auto font-mono text-sm text-green-400' title={temItemForaDeVigencia(vigente, contratado) ? `Contratado R$ ${fmt(contratado)}; vale hoje R$ ${fmt(vigente)}` : undefined}>
              MRR R$ {fmt(mrr)}
            </span>
          </>
        )
      }
    >
      {noGrupo && painel ? (
        <>
          <BarraDoGrupo p={painel} nome={nomeDoCliente} detalhado />
          <p className='mt-3 text-xs text-slate-500'>
            Cada empresa segue com a própria assinatura, fatura, nota e boleto. Esta aba é só leitura; o rateio se edita em Grupos Econômicos.
          </p>
        </>
      ) : (
        <>
          {contract.status === 'Suspenso' && (
            <div className={`mb-3 rounded-lg border px-3 py-2 text-sm ${TONE_BORDER.warning} ${TONE_SURFACE.warning}`}>
              <span className={`font-medium ${TONE_TEXT.warning}`}>Assinatura suspensa</span>
              {contract.suspendedReason && <span className='text-slate-300'> · {contract.suspendedReason}</span>}
              {contract.suspendedAt && <span className='text-slate-400 text-xs'> · desde {fmtDate(contract.suspendedAt)}</span>}
              {contract.reactivationDate && <span className='text-slate-400 text-xs'> · reativação prevista {fmtDate(contract.reactivationDate)}</span>}
            </div>
          )}

          {items.length === 0 ? (
            <p className='text-center text-slate-500 py-8'>{locationName ? `Nenhum produto para ${locationName}` : 'Nenhum produto nesta assinatura'}</p>
          ) : (
            <>
              {mrrItems.length > 0 && (
                <div>
                  {psItems.length > 0 && <div className={`text-[10px] uppercase tracking-wider mb-1 ${TONE_TEXT.open}`}>Recorrentes (MRR)</div>}
                  {items.map((i, idx) => (tipoDoItem(i, products) === 'MRR' ? linha(i, idx, false) : null))}
                </div>
              )}
              {/* DDRs logo abaixo dos itens, em chips, e não um bloco no fim. */}
              {ddrs.length > 0 && (
                <div className='mt-3 flex flex-wrap items-center gap-1.5'>
                  {/* DDR é consumo: azul (§12.4). O ciano saiu do HUB como acento (§4). */}
                  <span className={`text-[10px] uppercase tracking-wider mr-1 ${TONE_TEXT.progress}`}>DDRs ({ddrs.length})</span>
                  {ddrs.map((d: any) => (
                    <span key={d.id} className={`font-mono text-xs rounded-full px-2 py-0.5 border ${TONE_TEXT.progress} ${TONE_SURFACE.progress} ${TONE_BORDER.progress}`} title={d.city || d.state || undefined}>
                      {d.phoneNumber}
                    </span>
                  ))}
                </div>
              )}
              {psItems.length > 0 && (
                <div className='mt-4'>
                  <div className={`text-[10px] uppercase tracking-wider mb-1 ${TONE_TEXT.ps}`}>Serviços (P&S)</div>
                  {items.map((i, idx) => (tipoDoItem(i, products) !== 'MRR' ? linha(i, idx, true) : null))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </CascaDeModal>
  );
};

export default FichaDaAssinatura;
